import { Column, Columns, EmptyState, FlexBox } from '@dixa/lib-shared-habla';
import produce from 'immer';
import type { SearchParameters } from 'lib-search-types';
import { ampli } from 'lib-shared-tracking';
import { ReactNode, useCallback, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { Writable } from 'ts-essentials';

import { searchApi } from 'apis/search';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useAppPathParams } from 'utils/url/useAppPathParams';

import { SearchFilters } from './components/filters/searchFilters';
import { SearchFiltersProvider } from './components/filters/searchFilters.context';
import { filterFieldsPerTab } from './components/filters/utils/definitions';
import { SearchInput } from './components/input/searchInput';
import { SearchResultsLoading } from './components/results/components/loading/searchResultsLoading';
import { SearchResults } from './components/results/searchResults';
import { SavedSearchButton } from './components/savedSearch/button/savedSearchButton';
import { SavedSearchFormModal } from './components/savedSearch/form/savedSearchFormModal';
import { SavedSearchListModal } from './components/savedSearch/list/savedSearchListModal';
import { SearchTabs } from './components/tabs/searchTabs';
import { searchActions } from './search.actions';
import type { SearchViewScope } from './search.types';
import styles from './search.view.module.scss';
import { isLeafFilterCondition } from './utils/filters/filterTypeGuards';
import { createSearchUrlParams } from './utils/url/createSearchUrl';
import { useSearchInit } from './utils/useSearchInit/useSearchInit';

export const SearchView = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();

  const { isInitialized } = useSearchInit();
  const { scope } = useAppPathParams('/search/:scope');
  const { params } = useAppSelector((state) => state.search);

  const [readSearchResultsQuery, { data, isLoading, isError }] =
    searchApi.useLazyReadAdvancedSearchResultsQuery();

  const readSearchResultsQueryResultRef =
    useRef<ReturnType<typeof readSearchResultsQuery>>();

  const readSearchResults = useCallback(() => {
    if (readSearchResultsQueryResultRef.current) {
      readSearchResultsQueryResultRef.current.abort();
    }

    readSearchResultsQueryResultRef.current = readSearchResultsQuery({
      ...params,
      scope,
      enrichmentSpec: 'ALL',
    } as SearchParameters<'conversations', true>);
  }, [params, readSearchResultsQuery, scope]);

  useEffect(() => {
    if (isInitialized) {
      readSearchResults();
    }
  }, [isInitialized, readSearchResults]);

  const updateSearchParams = useCallback(
    (newParams: Partial<SearchParameters<'conversations'>>) => {
      dispatch(searchActions.params.update({ ...newParams, page: 1 }));
    },
    [dispatch],
  );

  const handleChangeScope = useCallback(
    (newScope: SearchViewScope) => {
      if (scope === newScope) return;

      const nextParams = produce(params, (draft) => {
        (draft.scope as SearchViewScope) = newScope;

        draft.filters.filters = draft.filters.filters.filter(
          (condition) =>
            isLeafFilterCondition(condition) &&
            filterFieldsPerTab[draft.scope].includes(condition.field),
        );

        delete draft.sortBy;
      });

      updateSearchParams(nextParams);
      navigate({
        path: '/search/:scope',
        params: createSearchUrlParams(nextParams),
      });

      ampli.searchTabOpened({
        tab: newScope,
      });
    },
    [navigate, params, scope, updateSearchParams],
  );

  const handleSubmitInput = useCallback(
    (query: string, isStrict: boolean) => {
      const nextParams: Partial<Writable<SearchParameters<'conversations'>>> = {
        query,
        isStrict,
      };

      if (!query) {
        nextParams.isStrict = false;
      }
      updateSearchParams(nextParams);

      ampli.searchFieldSubmitted({
        location: 'searchPage',
        isEmpty: !query,
        query,
      });
    },
    [updateSearchParams],
  );

  const handleClearInput = useCallback(() => {
    updateSearchParams({ query: '', isStrict: false });

    if (!params.filters.filters.length) {
      dispatch(searchActions.savedSearch.close());
    }

    ampli.searchFieldCleared({ location: 'searchPage' });
  }, [dispatch, params.filters.filters.length, updateSearchParams]);

  const handleClearSavedSearch = useCallback(() => {
    dispatch(searchActions.savedSearch.close());

    navigate({
      path: '/search/:scope',
      params: createSearchUrlParams({ scope }),
    });

    ampli.savedSearchClosed();
  }, [dispatch, navigate, scope]);

  if (!scope || !['conversations', 'users', 'companies'].includes(scope)) {
    return <Navigate to="/search/conversations" replace />;
  }

  const renderContent = (): ReactNode => {
    if (!isInitialized || isLoading) {
      return <SearchResultsLoading />;
    }

    if (isError) {
      return (
        <FlexBox
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          <EmptyState
            title="Something went wrong"
            description="We're experiencing some issues, try again or reload the page. If this issue persists, please contact support."
            icon="exclamation"
            iconColor="red.500"
            buttonProps={{
              children: 'Try again',
              onClick: readSearchResults,
            }}
          />
        </FlexBox>
      );
    }

    return <SearchResults onChangeScope={handleChangeScope} />;
  };

  return (
    <Columns>
      <Column>
        <Column.Row className={styles.inputRow} noMinHeight>
          <SearchInput
            onSearch={handleSubmitInput}
            onClear={handleClearInput}
            onSavedSearchClear={handleClearSavedSearch}
          />
        </Column.Row>

        <Column.Row
          slottable
          borderBottom
          noMinHeight
          noVerticalPadding
          className={styles.tabsRow}
        >
          <Column.Slot placement="start" className={styles.tabsSlot}>
            <SearchTabs
              totalResults={data?.meta.total_results}
              onChangeScope={handleChangeScope}
            />
          </Column.Slot>

          <Column.Slot placement="end">
            <FlexBox gap="200">
              <SearchFiltersProvider>
                <SearchFilters />
              </SearchFiltersProvider>

              <SavedSearchButton
                buttonProps={{
                  variant: 'secondary',
                }}
              />
            </FlexBox>
          </Column.Slot>
        </Column.Row>

        <Column.Row size={1} noVerticalPadding noHorizontalPadding>
          {renderContent()}
        </Column.Row>

        <SavedSearchFormModal />
        <SavedSearchListModal />
      </Column>
    </Columns>
  );
};
